"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useWorkflows } from "@/stores/appStore";
import {
  getTrackedSummaryTasks,
  publishTaskNotification,
  resolveSummaryTaskNotificationOutcome,
  untrackSummaryTask,
  createTaskNotificationDeduper,
  type PendingSummaryTask,
} from "@/lib/task-notifications";

const POLLING_INTERVAL_MS = 4000;

function getNotificationContent(task: PendingSummaryTask, succeeded: boolean) {
  if (task.stage === "outline") {
    return {
      taskType: "outline-generation" as const,
      title: succeeded ? "文献综述大纲生成完成" : "文献综述大纲生成失败",
      body: succeeded
        ? "文献综述大纲已生成，点击前往查看。"
        : "文献综述大纲生成失败，点击前往查看。",
    };
  }

  return {
    taskType: "literature-review" as const,
    title: succeeded ? "文献综述初稿生成完成" : "文献综述初稿生成失败",
    body: succeeded
      ? "文献综述初稿已生成，点击前往查看。"
      : "文献综述初稿生成失败，点击前往查看。",
  };
}

function getTaskHref(task: PendingSummaryTask) {
  if (!task.workflowId) return undefined;

  return task.stage === "outline"
    ? `/workflow-creator/${task.workflowId}/${task.summaryId}/2`
    : `/workflow/${task.workflowId}`;
}

function isCurrentTaskPage(pathname: string, task: PendingSummaryTask) {
  if (!task.workflowId) return false;

  return pathname === `/workflow/${task.workflowId}`
    || pathname === `/workflow-creator/${task.workflowId}`
    || pathname.startsWith(`/workflow-creator/${task.workflowId}/${task.summaryId}`);
}

export function SummaryTaskNotificationBridge() {
  const pathname = usePathname();
  const { fetchSummaryDetail } = useWorkflows();
  const pollingRef = useRef(false);
  const terminalNotificationDeduperRef = useRef(createTaskNotificationDeduper());

  useEffect(() => {
    let disposed = false;
    const notifyTerminalTask = (task: PendingSummaryTask, succeeded: boolean) => {
      const key = `${task.summaryId}:${task.stage}:${succeeded ? "succeeded" : "failed"}`;
      if (!terminalNotificationDeduperRef.current.add(key)) return;

      if (!isCurrentTaskPage(pathname, task)) {
        const content = getNotificationContent(task, succeeded);
        void publishTaskNotification({
          ...content,
          taskId: task.summaryId,
          status: succeeded ? "succeeded" : "failed",
          href: getTaskHref(task),
        });
      }

      untrackSummaryTask(task.summaryId, task.stage);
    };

    const pollTrackedTasks = async () => {
      if (pollingRef.current) return;
      const tasks = getTrackedSummaryTasks();
      if (!tasks.length) return;

      pollingRef.current = true;
      try {
        await Promise.all(tasks.map(async (task) => {
          try {
            const detail = await fetchSummaryDetail({ id: task.summaryId, silent: true });
            if (disposed || !detail) return;
            const outcome = resolveSummaryTaskNotificationOutcome(task.stage, detail.step, detail.status);
            if (outcome === "succeeded") {
              notifyTerminalTask(task, true);
            } else if (outcome === "failed") {
              notifyTerminalTask(task, false);
            } else if (outcome === "stale") {
              // 与当前任务无关的旧阶段记录不再需要继续轮询。
              untrackSummaryTask(task.summaryId, task.stage);
            }
          } catch (error) {
            console.warn("[Findin Task Notification] failed to check summary task:", {
              summaryId: task.summaryId,
              stage: task.stage,
              error,
            });
          }
        }));
      } finally {
        pollingRef.current = false;
      }
    };

    void pollTrackedTasks();
    const timer = window.setInterval(() => void pollTrackedTasks(), POLLING_INTERVAL_MS);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [fetchSummaryDetail, pathname]);

  return null;
}
